import axios from 'axios';
import { call, all, put, takeLatest, delay, select } from 'redux-saga/effects';
import notification from '../../../services/notification';
import history from '../../../services/history';
import mandatoryMixService from './mandatory-mix-service';
import * as mandatoryMixActions from './mandatory-mix-actions';
import {
  GET_ALL_REQUEST,
  GET_BY_ID_REQUEST,
  SAVE_REQUEST,
  EDIT_ITEM,
  DELETE_ITEM,
  GET_FILTER_PRODUCT_REQUEST
} from './mandatory-mix-constants';
import { ISA_ERROR } from '../../../constants';
import { formatMessage } from '~/i18n/translate';
import productService from '../product/product-service';
import * as productActions from '../product/product-actions';
import clientService from '../client/client-service';
import domainService from '~/services/modules/domain/domain-service';
import { appendSelectData } from '../select-pagination/select-pagination-actions';
import { ensureInputs } from './mandatory-mix-service';

const INITIAL_VALUES = {
  name: ''
};

const sort = items => {
  return [...items]?.sort((a: any, b: any) => a.externalCode - b.externalCode);
};

let cancel: any;
const CancelToken = axios.CancelToken;

export function* getAll() {
  try {
    yield put(mandatoryMixActions.setLoadingData(true));
    const { data } = yield call(mandatoryMixService.getAll);

    if (data) {
      yield put(mandatoryMixActions.getAllSuccess([...data]));
    }
    yield put(mandatoryMixActions.setLoadingData(false));
  } catch (error) {
    yield put(mandatoryMixActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* getById({ id }: any) {
  try {
    yield put(mandatoryMixActions.setLoadingData(true));

    const { externalCode: userExternalCode } = yield select((state: any) => state.auth.user);

    const [{ data: clientsData }, { data: clientGroups }, { data: subsidiaries }, { data: clientClassifications }] =
      yield all([
        call(clientService.getQuery, {
          skip: 0,
          limit: 0,
          fields: ['externalCode', 'name'],
          userExternalCode
        }),
        call(domainService.getClientGroups),
        call(domainService.getSubsidiaries, { userSubsidiaryFilter: [] }),
        call(clientService.getClassifications)
      ]);

    let selects = {
      clientsExternalCode: sort(clientsData),
      clientGroup: sort(clientGroups),
      subsidiariesExternalCode: sort(subsidiaries.map(({ name, externalCode }) => ({ externalCode, name }))),
      clientClassifications: clientClassifications
    };

    yield all([
      put(appendSelectData('client', selects.clientsExternalCode, true)),
      put(appendSelectData('domain/client-groups', selects.clientGroup, true)),
      put(appendSelectData('domain/subsidiaries', selects.subsidiariesExternalCode, true))
    ]);

    yield queryProducts({});

    if (!id) {
      yield put(
        mandatoryMixActions.getByIdSuccess({
          data: INITIAL_VALUES,
          selects
        })
      );
    } else {
      const { data } = yield call(mandatoryMixService.getMandatoryMix, id);

      if (data) {
        let populatedProducts = [];
        const productCodesToFetch = data.products?.map((p: any) => p.productExternalCode).join(',');

        if (productCodesToFetch && productCodesToFetch.length > 0) {
          const { data: productsData } = yield call(productService.getRawProducts, {
            skip: 0,
            limit: 0,
            productExternalCodes: productCodesToFetch
          });

          populatedProducts = productsData || [];
        }
        const ensuredSelects = yield* ensureInputs(data, selects, userExternalCode);
        yield put(
          mandatoryMixActions.getByIdSuccess({
            data: {
              ...data,
              clientsExternalCode: ensuredSelects.clients,
              subsidiariesExternalCode: ensuredSelects.subsidiaries,
              clientsGroupExternalCode: ensuredSelects.clientGroups,
              products:
                data.products?.map((p: any) => {
                  const fullProductObj = populatedProducts.find(
                    (fullProd: any) => fullProd.externalCode === p.productExternalCode?.toString()
                  );
                  return {
                    productExternalCode: p.productExternalCode,
                    quantity: p.quantity,
                    product: fullProductObj || null
                  };
                }) || []
            },
            selects
          })
        );
      }
    }
    yield put(mandatoryMixActions.setLoadingData(false));
  } catch (error) {
    yield put(mandatoryMixActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* save(data: any) {
  try {
    const { id, payload } = data;
    yield put(mandatoryMixActions.setLoadingSend(true));

    if (id) {
      yield call(mandatoryMixService.editMandatoryMix, { _id: id, ...payload });
    } else {
      yield call(mandatoryMixService.createMandatoryMix, payload);
    }
    yield call(history.push, `/engine/mandatory-mix/`, {
      date: new Date().getTime()
    });
    yield put(mandatoryMixActions.setLoadingSend(false));
    yield call(
      notification.success,
      id ? formatMessage('mandatoryMixFormUpdatedMessage') : formatMessage('mandatoryMixFormCreatedMessage')
    );
  } catch (error) {
    yield put(mandatoryMixActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* deleteItem({ id }: any) {
  try {
    const { isConfirmed } = yield call(
      notification.confirm,
      formatMessage('commonLabelExclude'),
      formatMessage('commonLabelExcludeItem')
    );
    if (isConfirmed) {
      yield put(mandatoryMixActions.setLoadingSend(true));
      yield call(mandatoryMixService.deleteMandatoryMix, id);
      yield put(mandatoryMixActions.setLoadingSend(false));

      yield put(mandatoryMixActions.getAllRequest());
      yield call(notification.success, formatMessage('mandatoryMixFormExcludeMessage'));
    }
  } catch (error) {
    yield put(mandatoryMixActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* editItem({ id }: any) {
  yield call(history.push, `/engine/mandatory-mix/edit/${id}`);
}

export function* queryProducts({ queryText = '' }: any) {
  yield delay(200);

  if (cancel !== undefined) cancel();

  const cancelToken = new CancelToken(function executor(c) {
    cancel = c;
  });

  try {
    yield put(productActions.setLoadingData(true));
    const { data: products } = yield call(
      productService.getRawProducts,
      {
        skip: 0,
        limit: 100,
        queryString: queryText
      },
      {
        cancelToken
      }
    );

    yield put(mandatoryMixActions.getFilterProductSuccess(products));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(productActions.setLoadingData(false));
  }
}

export default all([
  takeLatest(GET_ALL_REQUEST, getAll),
  takeLatest(GET_BY_ID_REQUEST, getById),
  takeLatest(SAVE_REQUEST, save),
  takeLatest(DELETE_ITEM, deleteItem),
  takeLatest(EDIT_ITEM, editItem),
  takeLatest(GET_FILTER_PRODUCT_REQUEST, queryProducts)
]);
